import { AUTH_TOKEN } from "../constants";

export const getAttachmentKey = (attachment: any, idx: number) =>
  String(attachment?.id ?? `idx-${idx}`);

export function sanitizeDownloadFileName(name?: string): string {
  const n = (name || "download").replace(/^.*[/\\]/, "").trim();
  return n || "download";
}

/** Re-type as octet-stream so the browser saves instead of opening PDF/image inline. */
export function forceSaveBlob(blob: Blob, fileName: string): void {
  const name = sanitizeDownloadFileName(fileName);
  const asFile = new Blob([blob], { type: "application/octet-stream" });
  const objectUrl = URL.createObjectURL(asFile);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = name;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

function parseDataUrlToOctetBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(",");
  if (comma === -1) throw new Error("Invalid data URL");
  const header = dataUrl.slice(0, comma);
  const data = dataUrl.slice(comma + 1);
  if (/;base64/i.test(header)) {
    const binary = atob(data.replace(/\s/g, ""));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: "application/octet-stream" });
  }
  return new Blob([decodeURIComponent(data)], {
    type: "application/octet-stream",
  });
}

function looksLikeRawBase64(s: string): boolean {
  const t = s.trim();
  if (t.length < 40) return false;
  if (/^(https?:|data:|blob:|[./])/i.test(t)) return false;
  return /^[A-Za-z0-9+/=\r\n]+$/.test(t);
}

function rawBase64ToOctetBlob(b64: string): Blob {
  const clean = b64.replace(/\s/g, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "application/octet-stream" });
}

/** Same host as RTK `dynamicBaseQuery` (avoids resolving `v1/...` against the SPA origin). */
function getDynamicApiOrigin(): string {
  const tenant = window.location.hostname.split(".")[0];
  const base = process.env.REACT_APP_BASE_URL || "";
  const apiPort = process.env.REACT_APP_API_PROTOCOL || "https";
  return `${apiPort}://${tenant}.${base}`.replace(/\/+$/, "");
}

function urlHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function fetchAttachmentBlob(
  resolvedUrl: string,
  sourceWasRelative: boolean
): Promise<Blob> {
  const apiHost = urlHostname(getDynamicApiOrigin());
  const fileHost = urlHostname(resolvedUrl);
  const sameApiHost = Boolean(apiHost && fileHost && fileHost === apiHost);
  const useAuth = sourceWasRelative || sameApiHost;

  const headers: Record<string, string> = {};
  if (useAuth) {
    const token = localStorage.getItem(AUTH_TOKEN);
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  return fetch(resolvedUrl, {
    method: "GET",
    headers,
    credentials: "omit",
  }).then((res) => {
    if (!res.ok) throw new Error(String(res.status));
    return res.blob();
  });
}

function resolveHttpAttachmentSource(source: string): {
  url: string;
  sourceWasRelative: boolean;
} {
  const t = source.trim();
  if (/^https?:\/\//i.test(t)) {
    return { url: t, sourceWasRelative: false };
  }
  if (t.startsWith("//")) {
    return {
      url: `${window.location.protocol}${t}`,
      sourceWasRelative: false,
    };
  }
  const origin = getDynamicApiOrigin();
  const path = t.startsWith("/") ? t : `/${t}`;
  return { url: `${origin}${path}`, sourceWasRelative: true };
}

export function getAttachmentFileBlob(source: string): Promise<Blob> {
  if (!source) return Promise.reject(new Error("empty"));

  if (source.startsWith("data:")) {
    try {
      return Promise.resolve(parseDataUrlToOctetBlob(source));
    } catch {
      return Promise.reject(new Error("parse"));
    }
  }

  if (looksLikeRawBase64(source)) {
    try {
      return Promise.resolve(rawBase64ToOctetBlob(source));
    } catch {
      return Promise.reject(new Error("b64"));
    }
  }

  if (source.startsWith("blob:")) {
    return fetch(source).then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.blob();
    });
  }

  const { url: resolved, sourceWasRelative } =
    resolveHttpAttachmentSource(source);
  return fetchAttachmentBlob(resolved, sourceWasRelative);
}